import type { ReactNode } from 'react';
import { Focusable } from 'react-aria-components';

import { Tooltip } from '@/lib/base/tooltip/tooltip';
import { cx } from '@/utils/cx';

/**
 * De-emphasises a proposal entity preview (a chip, a stage badge, a card)
 * that won't be applied, and explains why on hover and to screen readers.
 *
 * The wrapper is always mounted so a later toggle de-emphasises in place
 * instead of remounting the preview (which would lose its store subscription
 * state). The tooltip and the tab stop are only live while `excluded`.
 *
 * `Focusable` makes the wrapper the tooltip's trigger without adding a
 * button around content that may itself contain one (the chip's own link),
 * which would be invalid HTML.
 */
export function ExcludedOverlay({
  excluded,
  message,
  className,
  children,
}: {
  excluded: boolean;
  /** Why it won't be applied, e.g. "Won't be linked – action 3 (Acme) is unchecked." */
  message: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <Tooltip title={message} placement='top' isDisabled={!excluded}>
      <Focusable excludeFromTabOrder={!excluded} isDisabled={!excluded}>
        <div
          role='group'
          data-excluded={excluded || undefined}
          className={cx(excluded && 'opacity-50 grayscale', className)}
        >
          {children}
          {excluded && <span className='sr-only'>{message}</span>}
        </div>
      </Focusable>
    </Tooltip>
  );
}
